import { Dialog, DialogProps } from '@mui/material';
import useViewportMode, { ViewportMode } from '../../hooks/useViewportMode';

export type TabletFullScreenPolicy = 'always' | 'portrait' | 'never';
export type DialogWidthTier = 'compact' | 'comfortable' | 'wide' | 'max';
export type DialogSizingStrategy = 'content' | 'workspace';

const widthTierPixels: Record<DialogWidthTier, number> = {
    compact: 800,
    comfortable: 1100,
    wide: 1440,
    max: 1920,
};

export interface ResponsiveDialogViewportOverride {
    mode: ViewportMode;
    isPortrait?: boolean;
}

export interface ResponsiveDialogProps extends DialogProps {
    /**
     * Controls how a dialog uses the larger tablet viewport. Phones are always
     * full-screen; desktop continues to honor the caller's `fullScreen` prop.
     */
    tabletFullScreen?: TabletFullScreenPolicy;
    /** Intended for deterministic tests and embedded previews. */
    viewportOverride?: ResponsiveDialogViewportOverride;
    /** Lets complex desktop workspaces grow with the viewport while retaining a safe outer gutter. */
    desktopViewportGutter?: number;
    /** Maximum useful width for the dialog's content. */
    widthTier?: DialogWidthTier;
    /**
     * Content dialogs size to their contents and stop at the selected tier.
     * Workspace dialogs deliberately consume their tier so dense grids do not wrap.
     */
    sizingStrategy?: DialogSizingStrategy;
}

const ResponsiveDialog = ({
    fullScreen = false,
    tabletFullScreen = 'portrait',
    viewportOverride,
    desktopViewportGutter,
    widthTier,
    sizingStrategy = 'content',
    PaperProps,
    ...dialogProps
}: ResponsiveDialogProps) => {
    const viewport = useViewportMode();
    const mode = viewportOverride?.mode ?? viewport.mode;
    const isPortrait = viewportOverride?.isPortrait ?? viewport.isPortrait;

    const tabletRequestsFullScreen = tabletFullScreen === 'always'
        || (tabletFullScreen === 'portrait' && isPortrait);
    const adaptiveFullScreen = mode === 'phone'
        ? true
        : mode === 'tablet'
            ? fullScreen || tabletRequestsFullScreen
            : fullScreen;
    const inheritedPaperSx = PaperProps?.sx === undefined
        ? []
        : Array.isArray(PaperProps.sx)
            ? [...PaperProps.sx]
            : [PaperProps.sx];
    const containedGutter = desktopViewportGutter ?? (mode === 'tablet' ? 16 : 24);
    const tierWidth = widthTier ? widthTierPixels[widthTier] : undefined;
    const containedWidth = tierWidth === undefined
        ? mode === 'desktop' && desktopViewportGutter !== undefined ? `calc(100% - ${containedGutter * 2}px)` : undefined
        : `min(${tierWidth}px, calc(100vw - ${containedGutter * 2}px))`;
    // The legacy gutter-only opt-in has always meant "fill the available
    // desktop workspace". Preserve that contract while tiered dialogs default
    // to sizing from their contents.
    const effectiveSizingStrategy = tierWidth === undefined && desktopViewportGutter !== undefined
        ? 'workspace'
        : sizingStrategy;
    const containedSizing = containedWidth === undefined
        ? undefined
        : effectiveSizingStrategy === 'workspace'
            ? { width: containedWidth, maxWidth: containedWidth }
            : { width: 'fit-content', maxWidth: containedWidth };

    const viewportHeight = {
        height: '100vh',
        minHeight: '100vh',
        maxHeight: '100vh',
        '@supports (height: 100dvh)': {
            height: '100dvh',
            minHeight: '100dvh',
            maxHeight: '100dvh',
        },
    } as const;

    return (
        <Dialog
            {...dialogProps}
            fullScreen={adaptiveFullScreen}
            data-viewport-mode={mode}
            data-viewport-orientation={isPortrait ? 'portrait' : 'landscape'}
            data-dialog-width-tier={widthTier}
            data-dialog-sizing-strategy={effectiveSizingStrategy}
            PaperProps={{
                ...PaperProps,
                sx: [
                    ...inheritedPaperSx,
                    ...(!adaptiveFullScreen && containedSizing !== undefined ? [{
                        ...containedSizing,
                        margin: `${containedGutter}px`,
                    }] : []),
                    ...(adaptiveFullScreen ? [{
                        ...viewportHeight,
                        margin: 0,
                        overflow: 'hidden',
                        borderRadius: '0 !important',
                    }] : []),
                ] as any,
            }}
        />
    );
};

export default ResponsiveDialog;
